import { useCallback, useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { CaretLeftIcon, CaretRightIcon } from '@radix-ui/react-icons';
import {
  findIndexWithLargestDetectionArea,
  getMiddleElement,
} from '@/utils/trainers';
import { IDetection } from '@/types/features';
import { colors } from '@/utils/styles';
interface Props {
  images: string[];
  detections?: IDetection[];
  SVGOverlay?: string;
  onSelectedImageChange?: (index: number) => void;
  onImageLoaded?: (imageHeight: number) => void;
  startWithIndex?: 'middle' | 'largestDetection' | 'first';
  useNumberIndicator?: boolean;
}

export default function ImageCarousel({
  images = [],
  SVGOverlay,
  detections = [],
  onSelectedImageChange,
  onImageLoaded,
  startWithIndex = 'middle', // start with the middle image by default
  useNumberIndicator,
}: Props) {
  const totalImages = images.length;
  const midImageIndex = getMiddleElement(images);
  const largestDetectionIndex = findIndexWithLargestDetectionArea(detections);
  const startingIndex =
    startWithIndex === 'largestDetection' && largestDetectionIndex >= 0
      ? largestDetectionIndex
      : startWithIndex === 'first'
        ? 0
        : midImageIndex;
  const [currentFrame, setCurrentFrame] = useState(startingIndex);
  const imageRef = useRef<HTMLImageElement>(null);
  const startingImage = images[startingIndex];

  const handleNext = useCallback(() => {
    setCurrentFrame(prevFrame => {
      if (prevFrame + 1 < images.length) {
        return prevFrame + 1;
      } else {
        return 0;
      }
    });
  }, [images.length]);

  const handlePrev = useCallback(() => {
    setCurrentFrame(prevFrame => {
      if (prevFrame - 1 >= 0) {
        return prevFrame - 1;
      } else {
        return images.length - 1;
      }
    });
  }, [images.length]);

  const eventKeyCallback = useCallback(
    (event: any) => {
      switch (event.key) {
        case 'ArrowLeft':
          handlePrev();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        default:
          break;
      }
    },
    [handleNext, handlePrev],
  );

  useEffect(() => {
    document.addEventListener('keydown', eventKeyCallback, false);
    return () => {
      document.removeEventListener('keydown', eventKeyCallback, false);
    };
  }, [eventKeyCallback]);

  useEffect(() => {
    if (images?.length > 1) {
      setCurrentFrame(startingIndex);
    }
  }, [images?.length, startingIndex]);

  useEffect(() => {
    onSelectedImageChange?.(currentFrame);
  }, [currentFrame, onSelectedImageChange]);

  useEffect(() => {
    const handleResize = () => {
      if (startingImage) {
        // Create an image element and load the image asynchronously
        const _image = new Image();
        _image.src = startingImage;

        // Wait for the image to load
        _image.onload = () => {
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              const ref = imageRef?.current;
              if (ref && ref.clientHeight) {
                onImageLoaded?.(ref.clientHeight);
              }
            });
          });
        };
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [startingImage, onImageLoaded]);

  return (
    <div className="flex size-full flex-col items-center justify-center">
      <div className="relative size-full overflow-hidden">
        {totalImages
          ? Array.from({ length: totalImages }).map((_, index) => {
              const image = images?.[index];
              const detection = detections?.[index];
              const margin = 0.5;
              const centerX = detection?.[0] || 0;
              const centerY = detection?.[1] || 0;
              const width = detection?.[2] || 0;
              const height = detection?.[3] || 0;
              const w = (width + width * margin) * 100;
              const h = (height + height * margin) * 100;
              const x = centerX * 100 - w / 2;
              const y = centerY * 100 - h / 2;
              return (
                <div
                  key={index}
                  data-active={currentFrame === index}
                  className="flex h-full justify-center data-[active=false]:hidden">
                  {SVGOverlay && (
                    <img
                      src={SVGOverlay}
                      alt="overlay"
                      className="absolute left-0 top-0 z-10 size-full"
                    />
                  )}
                  <div className="react-zoom-children h-full">
                    <TransformWrapper>
                      <TransformComponent>
                        <img
                          className="flex h-full max-w-full items-center justify-center bg-neutral-900 object-contain"
                          src={image}
                          ref={index === startingIndex ? imageRef : null}
                          alt="frame"
                        />

                        {detection ? (
                          <div
                            className="absolute border-2 border-electricBlue-600"
                            style={{
                              left: `${x}%`,
                              top: `${y}%`,
                              width: `${w}%`,
                              height: `${h}%`,
                            }}
                          />
                        ) : null}
                      </TransformComponent>
                    </TransformWrapper>
                  </div>
                </div>
              );
            })
          : null}
      </div>
      {images?.length > 1 ? (
        <div className="flex min-h-12 w-full">
          <div className="flex w-full flex-col items-center justify-center px-4">
            <div
              className="flex w-full items-center justify-between p-1"
              style={{
                maxWidth: totalImages * (useNumberIndicator ? 40 : 25) + 80,
              }}>
              {
                <CaretLeftIcon
                  className="size-10 cursor-pointer transition hover:opacity-90"
                  onClick={() => handlePrev()}
                />
              }
              <div className="mx-3 flex flex-1 justify-between">
                {images.map((_: any, index: number) => (
                  <div
                    key={index}
                    data-active={currentFrame === index}
                    className="w-full bg-neutral-700 data-[active=true]:bg-neutral-0"
                    style={{
                      height: 5,
                      maxWidth: 5,
                      borderRadius: '50%',
                      ...(useNumberIndicator ? indicatorNumberStyles : {}),
                    }}>
                    {' '}
                    {useNumberIndicator ? index + 1 : ''}
                  </div>
                ))}
              </div>
              {
                <CaretRightIcon
                  className="size-10 cursor-pointer transition hover:opacity-90"
                  onClick={() => handleNext()}
                />
              }
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const indicatorNumberStyles = {
  width: 24,
  height: 24,
  marginLeft: '4px',
  marginRight: '4px',
  padding: '2px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  fontWeight: 600,
  color: colors.neutral[1000],
  maxWidth: 'none',
};
